interface ComingSoonProps {
  title: string
  /** What the report will answer, in the words a manager would use. */
  description: string
  /** The columns or figures it will carry, so the shape is not a surprise. */
  covers: string[]
  /**
   * What is missing, plainly.
   *
   * Named rather than hidden behind "coming soon", because the two cases are
   * different in a way that matters: a report waiting on a screen will arrive on
   * its own, and a report waiting on data somebody has to start recording will
   * not arrive until they do. Only one of those is worth a merchant's patience.
   */
  blockedBy: string
}

/**
 * A report that is specified but not yet backed.
 *
 * Shown instead of an empty table, and never instead of a real one. The whole
 * risk with a tab like this is that it reads as "no sales this period" — a
 * grid of dashes is indistinguishable from a quiet month, and somebody will act
 * on it. So this states plainly that nothing is being measured yet, what the
 * report will hold when it is, and what it is waiting on.
 */
export function ComingSoon({ title, description, covers, blockedBy }: ComingSoonProps) {
  return (
    <section className="border-border bg-surface flex flex-col gap-4 rounded-2xl border border-dashed p-6">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-sm font-medium">{title}</h2>
        <span className="text-muted border-border rounded-full border px-2 py-0.5 text-[0.7rem] font-medium tracking-wide uppercase">
          Coming soon
        </span>
      </div>

      <p className="text-muted max-w-2xl text-sm">{description}</p>

      <div className="flex flex-col gap-2">
        <h3 className="text-muted text-xs font-medium tracking-wide uppercase">Will show</h3>
        <ul className="flex flex-wrap gap-1.5">
          {covers.map((item) => (
            <li
              key={item}
              className="border-border bg-background text-muted rounded-full border px-2.5 py-1 text-xs"
            >
              {item}
            </li>
          ))}
        </ul>
      </div>

      <p className="text-muted border-border border-t pt-3 text-xs">
        <span className="text-foreground font-medium">Waiting on:</span> {blockedBy}
      </p>
    </section>
  )
}
